import { Card, Grid, Header } from '@mairistem/composants';
import {
	fetchModuleActif,
	Module,
	Produit,
	UserRole,
} from '@mairistem/finances-utils';
import React from 'react';
import type BaseOutil from '../../classes/outils/BaseOutil';
import CalculRAR from '../../classes/outils/CalculRAR';
import CalculVirementCredit from '../../classes/outils/CalculVirementCredit';
import ControleCoherence from '../../classes/outils/ControleCoherence';
import ImportExportExcel from '../../classes/outils/ImportExportExcel';
import RecopieReport from '../../classes/outils/RecopieReport';
import type ApercuNotePresentation from '../../interfaces/simulation/apercuNotePresentation';
import type Simulation from '../../interfaces/simulation/simulation';
import Outil from './Outils/Outil';
import './BoiteOutil.css';
import RecuperationDonneeConnexe from '../../classes/outils/RecuperationDonneeConnexe';
import RedigerNotePresentation from '../../classes/outils/RedigerNotePresentation';
import StorageKey from '../../constants/common/storage';

interface BoiteOutilProps {
	onApercuNote: (apercu: ApercuNotePresentation) => void;
	onRefresh: () => void;
	simulation: Simulation;
	userRole: UserRole;
}

const BoiteOutil = ({
	onApercuNote,
	onRefresh,
	simulation,
	userRole = UserRole.NORMAL,
}: BoiteOutilProps) => {
	const [open, setOpen] = React.useState<boolean>(false);
	const [selectedOutil, setSelectedOutil] = React.useState<BaseOutil>();
	const [hasModuleNotePresentation, setHasModuleNotePresentation] =
		React.useState<boolean>(false);

	React.useEffect(() => {
		const getModuleActif = async () => {
			setHasModuleNotePresentation(
				await fetchModuleActif(
					StorageKey.MODULE_NOTE_PRESENTATION,
					Produit.ELABORATION_BUDGET,
					Module.NOTE_PRESENTATION,
				),
			);
		};

		getModuleActif();
	}, []);

	const handleClickOutil = (outil: BaseOutil) => {
		setSelectedOutil(outil);
		setOpen(true);
	};

	const handleClose = () => setOpen(false);

	const handleValidate = () => {
		setOpen(false);
		onRefresh?.();
	};

	const outils: BaseOutil[] = [
		new RecopieReport(simulation),
		new RecuperationDonneeConnexe(),
		new ImportExportExcel(),
		new CalculVirementCredit(),
		new CalculRAR(),
		new ControleCoherence(simulation),
		new RedigerNotePresentation(hasModuleNotePresentation, onApercuNote),
	];

	return (
		<>
			<Card className="boiteOutil" fluid>
				<Card.Content>
					<Card.Header as={Header} content="BOÎTE À OUTILS" size="tiny" />
					<Card.Description>
						<Grid padded>
							<Grid.Row className="nowrap">
								{outils?.map((outil) => {
									if (!outil?.isEnabled(userRole, simulation)) return null;

									const isActive = outil?.isActive(userRole, simulation);

									return (
										<Grid.Column
											className={isActive ? undefined : 'outilColumnDisabled'}
											data-testid={`outil${outil?.code}`}
											onClick={
												isActive ? () => handleClickOutil(outil) : undefined
											}
											key={outil?.code}
											width={3}
										>
											<Outil
												disabled={!isActive}
												icon={outil?.icon}
												title={outil?.getTitle(simulation)}
											/>
										</Grid.Column>
									);
								})}
							</Grid.Row>
						</Grid>
					</Card.Description>
				</Card.Content>
			</Card>
			{selectedOutil?.renderModal(
				simulation,
				handleClose,
				handleValidate,
				open,
			)}
		</>
	);
};

export default BoiteOutil;
